/**
 * The prompt card: the country name at rung 1, the probe at rungs 2-4.
 *
 * Kept visually distinct between the two so the player never has to read to
 * know which kind of question they are being asked — at 8 seconds a rung, that
 * half second matters.
 */

import type { ReactNode } from 'react';
import type { GameMode, ProbeType } from '@geogame/shared';

import { rungTitle } from '../lib/format';
import { CountdownRing } from './CountdownRing';
import { Flame } from './Flame';

const PROBE_LABEL: Record<ProbeType | 'pinpoint', string> = {
  pinpoint: 'Find it on the globe',
  capital: 'Capital city',
  population: 'Population',
  flag: 'Flag',
  borders: 'Land borders',
  neighbour: 'Land neighbour',
  currency: 'Currency',
  language: 'Language',
  populous: 'Population, compared',
  languages: 'Languages, most spoken first',
  truefalse: 'True or false',
  numeric: 'Type the number',
  region: 'Region',
};

export interface PromptCardProps {
  readonly rung: number;
  readonly probeType: ProbeType | 'pinpoint';
  readonly prompt: string;
  readonly deadlineServerTs: number | null;
  readonly durationMs: number;
  readonly serverNow: () => number;
  /**
   * CHANGE 6. This card belongs to the golden round's paying rung. It is not
   * merely a tint: the kicker changes, a flame sits beside the countdown, and
   * the card's border burns. The owner's requirement was that the golden round
   * "should feel like an event", and a card that looked the same with a
   * different accent colour would not be one.
   */
  readonly golden?: boolean;
  readonly gameMode?: GameMode;
  readonly children?: ReactNode;
}

export function PromptCard({
  rung,
  probeType,
  prompt,
  deadlineServerTs,
  durationMs,
  serverNow,
  golden = false,
  gameMode = 'country',
  children,
}: PromptCardProps): React.JSX.Element {
  const isPinpoint = probeType === 'pinpoint';
  /**
   * The kicker names the mode, not just the probe. "Rung 1 · Region" is what
   * `PROBE_LABEL` would print for a subdivision pinpoint, and it is neither
   * wrong nor any use.
   */
  const kind =
    gameMode === 'city'
      ? isPinpoint
        ? 'Pin the city'
        : 'City trivia'
      : gameMode === 'state'
        ? isPinpoint
          ? 'Pin the state'
          : 'State trivia'
        : PROBE_LABEL[probeType];
  return (
    <section
      className="gg-prompt"
      data-kind={isPinpoint ? 'pinpoint' : 'probe'}
      data-golden={golden ? 'true' : 'false'}
    >
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className={golden ? 'gg-prompt-kicker gg-golden-kicker' : 'gg-prompt-kicker'}>
            {golden ? (
              <>
                <Flame size={13} /> Golden rung · {kind}
              </>
            ) : (
              <>
                {rungTitle(rung)} · {kind}
              </>
            )}
          </p>
          <h2 className={isPinpoint ? 'gg-prompt-country' : 'gg-prompt-question'}>{prompt}</h2>
        </div>
        <CountdownRing
          deadlineServerTs={deadlineServerTs}
          durationMs={durationMs}
          serverNow={serverNow}
          tone={golden ? 'golden' : 'neutral'}
          size={60}
        />
      </header>
      {children ? <div className="mt-3">{children}</div> : null}
    </section>
  );
}
